import React from 'react';
import {ConnectedNovaInvocationControl} from '../../nova-invocation/ConnectedNovaInvocationControl';
import {NovaAgentPickerForTask} from '../../nova-invocation/NovaAgentPickerForTask';
import {NOVA_RUN_SURFACE} from '../../nova-invocation/novaRunSurface';
import {FormattedMessage, injectIntl} from 'react-intl';
import {UnapprovedIcon} from '@/web-components';

import Util from '../../../util/util';
import {MODAL_TYPE, showModal} from '../../modals/generic_modal_conductor';
import UpdateTaskMutation from '../../../../../mutations/update_task_mutation.modern';
import PriorityTaskInputIconPath from '../../../../../images/ico-priority';
import BlockedTaskInputIconPath from '../../../../../images/ico-blocked';
import BugTaskInputIconPath from '../../../../../images/ico-bug';
import NonBillableTaskInputIconPath from '../../../../../images/ico-nonbillable';
import RepeatingTaskIcon from '../../../../../images/ico-repeating';
import Timer from '../../../../../components/timer';
import StarTask from '../../star/star_task';
import {dispatch, EVENT_ID} from '../../../../../containers/event_manager';
import {hasPermission, hasTaskUpdatePermission, isClientUser} from '../../../util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../../../Permissions';
import ProjectUtil from '../../../util/ProjectUtil';
import {HIDDEN_FEATURES} from '../../../../../constants';
import {canLoggedInPersonManageProgram, hasTopDownProgramBudgetFeature} from '../../../util/ProgramFinancialLogic';
import {ACTION} from '../../modals/program-management/ProgramBudgetErrorMessage';
import {trackEvent} from '../../../../../tracking/amplitude/TrackingV2';
import {createFragmentContainer, graphql} from 'react-relay';
import {isJiraToForecastOneWaySync} from '../../../util/JiraUtil';
import {isFeatureHidden} from '../../../util/CompanySetupUtil';
import {hasFeatureFlag} from '../../../util/FeatureUtil';
import {taskSetModalChipModel} from '../../../../project-tab/projects/scoping-page/task-set/taskSetModalChipModel';
import {TaskSetModalChip} from '../../../../project-tab/projects/scoping-page/task-set/TaskSetModalChip';

const optionsSection = props => {
	const {formatMessage} = props.intl;
	const {task, project, viewer, company, showTimer, disabled} = props;
	const {repeatingTask} = task;
	// Independent of the Repeat block below: a task can be both a repeating task
	// and a set instance, and AC6 says both indicators show.
	const taskSetChip = taskSetModalChipModel({
		featureFlagOn: hasFeatureFlag('recurring_tasks_set'),
		taskSet: task.taskSet,
		sequence: task.taskSetSequence,
	});
	const disableBillableChange = task.hasInvoicedTime && task.billable;

	const weekDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
	let selectedWeekDays = [];
	if (repeatingTask && repeatingTask.repeatType === 'WEEKLY') {
		selectedWeekDays = weekDays.filter(day => repeatingTask[day] === true);
	}

	const canEditTask = hasTaskUpdatePermission();
	// Billable predates TASK_UPDATE and was previously gated on PHASE_UPDATE alone; that audience
	// (Coordinator+) must be preserved, so both permissions are required rather than TASK_UPDATE
	// substituting for PHASE_UPDATE. TASK_UPDATE defaults on for Collaborators (who never had
	// PHASE_UPDATE), so TASK_UPDATE alone would hand this action to a wider audience than before.
	const canToggleBillable = canEditTask && hasPermission(PERMISSION_TYPE.PHASE_UPDATE);

	const togglePriority = () => {
		if (disabled || !canEditTask) return;

		Util.CommitSchedulingModalUpdate(
			UpdateTaskMutation,
			{
				ids: [task.id],
				highPriority: !task.highPriority,
			},
			() => dispatch(EVENT_ID.SHOW_CHANGES_SAVED)
		);
	};

	const toggleBlock = () => {
		if (disabled) return;

		Util.CommitSchedulingModalUpdate(
			UpdateTaskMutation,
			{
				ids: [task.id],
				blocked: !task.blocked,
			},
			() => dispatch(EVENT_ID.SHOW_CHANGES_SAVED)
		);
	};

	const toggleBug = () => {
		if (disabled || !canEditTask) return;

		Util.CommitSchedulingModalUpdate(
			UpdateTaskMutation,
			{
				ids: [task.id],
				bug: !task.bug,
			},
			() => dispatch(EVENT_ID.SHOW_CHANGES_SAVED)
		);
	};

	const showProgramRevenueLockedMessage = () => {
		const program = project.program;
		const loggedInPersonId = viewer.actualPersonId;
		const canManageProgram = canLoggedInPersonManageProgram(program?.members, loggedInPersonId);

		showModal({
			type: MODAL_TYPE.PROGRAM_BUDGET_ERROR_MESSAGE,
			action: ACTION.MARK_TASK_BILLABLE,
			programName: program?.name,
			canManageProgram,
			programPrefix: program?.prefix,
		});
	};

	const onSuccess = result => {
		if (result.updateTask.errors && result.updateTask.errors.length === 1) {
			// The server refuses to mark a task billable while its program's revenue is locked, and the
			// checkbox reverts on its own; without this the revert carries no explanation.
			if (result.updateTask.errors[0] === 'PROGRAM_REVENUE_LOCKED') {
				showProgramRevenueLockedMessage();
			} else {
				Util.checkForSageErrorAndShowModal(result.updateTask.errors, false);
			}
		} else {
			dispatch(EVENT_ID.SHOW_CHANGES_SAVED);
		}
	};

	const toggleBillable = () => {
		if (disabled || disableBillableChange || !canToggleBillable) return;

		const newBillableStatus = !task.billable;
		if (hasTopDownProgramBudgetFeature() && newBillableStatus && project.isProgramRevenueLocked) {
			trackEvent('Task', 'Set As Billable', {location: 'From Options Section', error: 'Program Revenue Locked'});
			showProgramRevenueLockedMessage();
			return;
		}
		Util.localStorageDeleteFincancialMap();
		Util.CommitSchedulingModalUpdate(
			UpdateTaskMutation,
			{
				ids: [task.id],
				billable: newBillableStatus,
			},
			onSuccess
		);
	};

	const onIconKeyPress = (icon, e) => {
		if (e.key === 'Enter') {
			switch (icon) {
				case 'priority':
					togglePriority();
					break;
				case 'blocked':
					toggleBlock();
					break;
				case 'bug':
					toggleBug();
					break;
				case 'non-billable':
					toggleBillable();
					break;
				default:
					return;
			}
		}
	};

	const showRecurringTaskModal = () => {
		if (disabled) return;

		showModal({
			type: MODAL_TYPE.TASK_RECURRENCE,
			repeatingTask: task.repeatingTask,
			taskId: task.id,
		});
	};

	const handleRecurringKeyPress = e => {
		if (e.key === 'Enter') showRecurringTaskModal();
	};

	const sendToJira = () => {
		if (project.isJiraProject) {
			showModal({
				type: MODAL_TYPE.CREATE_JIRA_ISSUE,
				projectId: project.id,
				taskIds: [task.id],
				jiraType: project.jiraCloudProject || project.jiraCloudEpicIds?.length ? 'cloud' : 'server',
				useTaskHierarchy: project.useTaskHierarchy,
				hasParent: task.parentTaskId,
			});
		}
	};

	const sendToAdo = () => {
		if (project.vstsProject && project.vstsAccount) {
			showModal({
				type: MODAL_TYPE.CREATE_ADO_WORKITEM,
				projectId: project.id,
				vstsAccount: project.vstsAccount,
				vstsArea: project.vstsArea,
				taskIds: [task.id],
			});
		}
	};

	return (
		<div className={'task-modal-v3-options-section' + (disabled ? ' disabled' : '')}>
			<div className={'left-section'} data-userpilot={'task-modal-indicators'}>
				{task.approved ? null : <UnapprovedIcon text={formatMessage({id: 'common.unapproved'})} />}
				<PriorityTaskInputIconPath
					cy="icon-priority"
					title={
						!canEditTask
							? formatMessage(
									{id: 'permission.no_task_update_permission'},
									{fieldName: formatMessage({id: 'common.priority'})}
							  )
							: formatMessage({id: task.highPriority ? 'task-modal.unmark-priority' : 'task-modal.mark-priority'})
					}
					active={task.highPriority}
					className={disabled || !canEditTask ? 'disabled' : ''}
					onClick={togglePriority}
					onKeyDown={onIconKeyPress.bind(this, 'priority')}
				/>
				{project.isJiraProject ? null : (
					<BugTaskInputIconPath
						cy="icon-bug"
						title={
							!canEditTask
								? formatMessage(
										{id: 'permission.no_task_update_permission'},
										{fieldName: formatMessage({id: 'common.bug'})}
								  )
								: formatMessage({id: task.bug ? 'card_modal.unmark-as-bug' : 'card_modal.mark-as-bug'})
						}
						active={task.bug}
						className={(task.bug ? 'active-state ' : '') + (disabled || !canEditTask ? ' disabled' : '')}
						onClick={toggleBug}
						onKeyDown={onIconKeyPress.bind(this, 'bug')}
					/>
				)}
				{ProjectUtil.projectTracksRevenue(project) && canToggleBillable ? (
					<NonBillableTaskInputIconPath
						cy="icon-nonbillable"
						title={formatMessage({id: task.billable ? 'card_modal.unmark-billable' : 'card_modal.mark-billable'})}
						active={!task.billable}
						className={
							(!task.billable ? 'active-state ' : '') + (disabled || disableBillableChange ? ' disabled' : '')
						}
						onClick={toggleBillable}
						onKeyDown={onIconKeyPress.bind(this, 'non-billable')}
					/>
				) : null}
				<BlockedTaskInputIconPath
					cy="icon-blocked"
					title={formatMessage({id: task.blocked ? 'card_modal.unblock-card' : 'card_modal.block-card'})}
					active={task.blocked}
					className={disabled ? 'disabled' : ''}
					onClick={toggleBlock}
					onKeyDown={onIconKeyPress.bind(this, 'blocked')}
				/>

				{repeatingTask ? (
					<div
						className={'recurring-days' + (disabled || !canEditTask ? ' disabled' : '')}
						onClick={canEditTask ? showRecurringTaskModal : undefined}
						onKeyPress={canEditTask ? handleRecurringKeyPress.bind(this) : undefined}
						tabIndex="0"
						title={
							!canEditTask
								? formatMessage(
										{id: 'permission.no_task_update_permission'},
										{fieldName: formatMessage({id: 'common.recurrence'})}
								  )
								: undefined
						}
					>
						<RepeatingTaskIcon />
						<div className="repeat-text">{formatMessage({id: 'card_modal.start_recurring'})}:</div>
						{repeatingTask.repeatType === 'WEEKLY' && selectedWeekDays.length > 0 ? (
							<div className="repeating-days">
								{selectedWeekDays.map(day => (
									<span className="day" key={day}>
										{formatMessage({id: `common.${day}`}).substr(0, 3)}
									</span>
								))}
							</div>
						) : repeatingTask.repeatType === 'WEEKLY' && selectedWeekDays.length === 0 ? (
							<div className="repeating-days">
								<span className="day">
									<FormattedMessage id={'common.weekly'} />
								</span>
							</div>
						) : repeatingTask.repeatType === 'MONTHLY' && repeatingTask.monthlyDay ? (
							<span className="day">
								{repeatingTask.monthlyDay}. {formatMessage({id: `task_recurrence_modal.of-the-month`})}
							</span>
						) : null}
					</div>
				) : null}
				{taskSetChip ? (
					<div className="task-set-chip">
						{/* aria-hidden because the chip's own aria-label already opens with
						    "Task set: ...". Without this a screen reader reads the label
						    twice -- "Task set: Task set: Weekly check-in, 3 of 14". The text
						    stays for sighted readers, who get no label from the chip. */}
						<div className="task-set-text" aria-hidden="true">
							{formatMessage({id: 'task_set.modal_label'})}:
						</div>
						<TaskSetModalChip {...taskSetChip} />
					</div>
				) : null}
				{showTimer &&
				!disabled &&
				!isClientUser() &&
				!ProjectUtil.projectUsesManualProgress(project) &&
				!isFeatureHidden(HIDDEN_FEATURES.TIME_REGISTRATIONS) ? (
					<Timer
						key={viewer.timerTask && viewer.timerTask.id}
						taskModal={true}
						taskId={task.id}
						viewer={{...viewer, task}}
						userpilot={'task-modal-timer'}
					/>
				) : null}
			</div>
			{task.approved ? (
				<div className={'right-section'}>
					<StarTask
						task={task}
						parentId={viewer.id}
						cySource={'task-modal'}
						customSize={16}
						userpilot={'task-modal-star'}
					/>
				</div>
			) : null}
			{/* After the star rather than among the icons, so the Jira and ADO links sit between the
			    star and the Nova control instead of pushing the star away from the icons. */}
			{task.jiraKey || (task.vstsId && project.vstsProject && project.vstsAccount) ? (
				<div className="integration-link">
					{task.jiraKey ? (
						<a href={task.jiraKey} target="_blank" rel="noopener noreferrer">
							{formatMessage({id: 'task_modal.jira_issue'})}
						</a>
					) : null}
					{task.vstsId && project.vstsProject && project.vstsAccount ? (
						<a
							href={
								'https://dev.azure.com/' +
								project.vstsAccount +
								'/' +
								project.vstsProject +
								'/_workitems/edit/' +
								task.vstsId
							}
							target="_blank"
						>
							{formatMessage({id: 'task_modal.ado_workitem'})}
						</a>
					) : null}
				</div>
			) : null}
			{!isJiraToForecastOneWaySync(company) && !task.jiraKey && project.isJiraProject ? (
				<div className="send-to-jira-link" onClick={sendToJira.bind(this)}>
					{formatMessage({id: 'common.move_to_jira'})}
				</div>
			) : null}
			{!task.vstsId && project.vstsProject && project.vstsAccount && project.vstsTwoWaySync ? (
				<div className="send-to-jira-link" onClick={sendToAdo.bind(this)}>
					{formatMessage({id: 'common.move_to_ado'})}
				</div>
			) : null}
			{/* Outside the star's own section, which an unapproved task does not render: the control
			    belongs to every task, and nesting it there took it away from half of them. Last in
			    the row and growing into what is left, so it starts where the icons end. */}
			<div className="nova-invocation-container">
				{/* Starting a run from the task itself, rather than only from a list or board. The
			    same control the rows mount, in the modal's own treatment: one state machine,
			    so Action, Working, Ready and Continue cannot disagree between the two places a
			    person meets them. It gates itself on the feature flag and renders nothing when
			    the flag is off.

			    instanceKey is its own: the caret's anchor id and the picker's toggle events are
			    keyed by it, and the modal can be open over a list that has a control mounted for
			    this very task. */}
				<ConnectedNovaInvocationControl
					taskId={task.id}
					taskName={task.name}
					companyProjectId={project.companyProjectId}
					companyTaskId={task.companyTaskId}
					// Not task.hasNovaConversation alone: that is the value Relay fetched when the
					// modal opened, so a conversation discarded since still reads as present and
					// the control offers Continue for a thread that is no longer on screen.
					// Clicking it found nothing to open, and its caret toggled a picker that had
					// gone with the thread.
					hasConversation={task.hasNovaConversation && !props.novaThreadDiscarded}
					runningRunId={task.novaRunningRunId}
					// Continue waits on an error the conversation ended in: it has to be retried or
					// dismissed first, the same as Reply to Nova in the composer.
					newestTurnFailed={!!props.novaNewestTurnFailed}
					projectColor={project.projectColor}
					instanceKey="task-modal-header"
					surface={NOVA_RUN_SURFACE.TASK_MODAL}
					variant="header"
					// The two apart, not the row's own `disabled`, which joins them: a locked task and a
					// person restricted from client actions are different refusals, and only the second
					// one means this person cannot act at all - which is what keeps the promo card away.
					readOnly={!!props.isTaskReadOnly}
					isClientActionsRestricted={!!props.isClientActionsRestricted}
					canEditTask={canEditTask}
					// The task is already open, so there is nothing to navigate to: reading the
					// answer means bringing the thread it is in into view.
					onOpenTask={() => props.onOpenNovaThread && props.onOpenNovaThread()}
					agentPicker={
						<NovaAgentPickerForTask
							taskId={task.id}
							instanceKey="task-modal-header"
							surface={NOVA_RUN_SURFACE.TASK_MODAL}
							// The control sits at the right edge of the modal header, so the list
							// opens back across the modal rather than off its edge and over the
							// page behind it.
							align="right"
							taskName={task.name}
							companyProjectId={project.companyProjectId}
							companyTaskId={task.companyTaskId}
							projectColor={project.projectColor}
						/>
					}
				/>
			</div>
		</div>
	);
};

export default injectIntl(
	createFragmentContainer(optionsSection, {
		company: graphql`
			fragment optionsSection_company on Company {
				jiraCloudEnabled
				jiraServerEnabled
				integrations {
					jiraCloud {
						syncSettings {
							isJiraToForecastOneWaySync
						}
					}
					jiraServer {
						syncSettings {
							isJiraToForecastOneWaySync
						}
					}
				}
			}
		`,
	})
);
